import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminPageLayout } from '@/components/admin/shared/AdminPageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useClients } from '@/hooks/useClients';
import { clientApiService } from '@/services/api/client.service';
import { useToast } from '@/hooks/use-toast';
import type { Client } from '@/types/clients';
import { ClientFormDialog, type ClientFormData } from '@/components/admin/clients/ClientFormDialog';
import { DeleteClientDialog } from '@/components/admin/clients/DeleteClientDialog';
import {
  Plus,
  Grid3X3,
  List,
  Users,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdminClientLeadsTable } from '@/components/admin/clients/AdminClientLeadsTable';
import { AdminClientTracesTable } from '@/components/admin/clients/AdminClientTracesTable';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { tracesApiService } from '@/services/api/traces.service';

export function AdminClients() {
  const [view, setView] = useState<'grid' | 'table'>('grid');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'maintenance'>('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState<'all' | 'chatbot' | 'voicebot'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // PHASE 1: Expansion state management
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // CRUD Dialog states
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Handler to open client details
  const handleViewDetails = (client: Client) => {
    setSelectedClient(client);
    setIsDetailOpen(true);
  };

  // Fetch clients from API
  const {
    data: clientsData,
    isLoading,
    error,
    refetch,
    isRefetching
  } = useClients({
    ...(statusFilter !== 'all' && { status: statusFilter })
  });

  const clients = clientsData?.clients || [];

  // Fetch leads statistics for chatbot clients
  const { data: leadsStatsData } = useQuery({
    queryKey: ['client-leads-stats', selectedClient?.id],
    queryFn: async () => {
      if (!selectedClient || selectedClient.project_type !== 'chatbot') {
        return null;
      }
      // Get first page with limit 1 to get total and latest lead
      const response = await clientApiService.getClientLeads(selectedClient.id, {
        page: 1,
        limit: 1,
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      return {
        total: response.total,
        latestLead: response.data[0] || null
      };
    },
    enabled: !!selectedClient && selectedClient.project_type === 'chatbot',
    staleTime: 60 * 1000, // 1 minute
  });

  // Fetch traces statistics for voicebot clients
  const { data: voicebotStatsData } = useQuery({
    queryKey: ['client-voicebot-stats', selectedClient?.vapi_tag],
    queryFn: async () => {
      if (!selectedClient || selectedClient.project_type !== 'voicebot') {
        return null;
      }

      try {
        // Get traces for today to calculate daily metrics
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Fetch traces for today
        const todayResponse = await tracesApiService.listTraces({
          tags: [selectedClient.vapi_tag],
          fromTimestamp: today.toISOString(),
          limit: 100, // Get up to 100 traces to calculate cost
          page: 1
        });

        // Fetch the most recent trace (for "last call" metric)
        const recentResponse = await tracesApiService.listTraces({
          tags: [selectedClient.vapi_tag],
          limit: 1,
          page: 1
        });

        // Calculate total cost for today
        const totalCostToday = todayResponse.data.reduce((sum: number, trace: any) => {
          return sum + (trace.totalCost || 0);
        }, 0);

        return {
          totalCalls: recentResponse.meta.totalItems || 0,
          totalCallsToday: todayResponse.meta.totalItems || 0,
          totalCostToday,
          latestTrace: recentResponse.data[0] || null
        };
      } catch (error) {
        console.error('Error fetching voicebot stats:', error);
        // Return null on error to show "no data" state
        return null;
      }
    },
    enabled: !!selectedClient && selectedClient.project_type === 'voicebot',
    staleTime: 60 * 1000, // 1 minute
    retry: false, // Don't retry on error
  });

  // Mutation: Create client
  const createClientMutation = useMutation({
    mutationFn: (data: ClientFormData) => clientApiService.createClient(data),
    onSuccess: (newClient) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({
        title: 'Cliente creado',
        description: `El cliente "${newClient.name}" ha sido creado exitosamente.`,
      });
      setIsFormDialogOpen(false);
      setClientToEdit(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error al crear cliente',
        description: error.message || 'No se pudo crear el cliente. Intenta nuevamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Update client
  const updateClientMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ClientFormData> }) =>
      clientApiService.updateClient(id, data),
    onSuccess: (updatedClient) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (selectedClient?.id === updatedClient.id) {
        setSelectedClient(updatedClient as Client);
      }
      toast({
        title: 'Cliente actualizado',
        description: `El cliente "${updatedClient.name}" ha sido actualizado exitosamente.`,
      });
      setIsFormDialogOpen(false);
      setClientToEdit(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error al actualizar cliente',
        description: error.message || 'No se pudo actualizar el cliente. Intenta nuevamente.',
        variant: 'destructive',
      });
    },
  });

  // Mutation: Delete client
  const deleteClientMutation = useMutation({
    mutationFn: (clientId: string) => clientApiService.deleteClient(clientId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (selectedClient?.id === data.deletedClientId) {
        setIsDetailOpen(false);
        setSelectedClient(null);
      }
      toast({
        title: 'Cliente eliminado',
        description: `El cliente "${data.deletedClientName}" ha sido eliminado exitosamente.`,
      });
      setIsDeleteDialogOpen(false);
      setClientToDelete(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error al eliminar cliente',
        description: error.message || 'No se pudo eliminar el cliente. Verifica que no tenga leads o conversaciones asociadas.',
        variant: 'destructive',
      });
    },
  });

  // Handlers
  const handleCreateClient = () => {
    setClientToEdit(null);
    setIsFormDialogOpen(true);
  };

  const handleEditClient = (client: Client) => {
    setClientToEdit(client);
    setIsFormDialogOpen(true);
  };

  const handleDeleteClient = (client: Client) => {
    setClientToDelete(client);
    setIsDeleteDialogOpen(true);
  };

  const handleFormSubmit = async (data: ClientFormData) => {
    if (clientToEdit) {
      // Update mode
      await updateClientMutation.mutateAsync({ id: clientToEdit.id, data });
    } else {
      // Create mode
      await createClientMutation.mutateAsync(data);
    }
  };

  const handleDeleteConfirm = async () => {
    if (clientToDelete) {
      await deleteClientMutation.mutateAsync(clientToDelete.id);
    }
  };

  // Client-side filtering for search and project type
  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProjectType = projectTypeFilter === 'all' || client.project_type === projectTypeFilter;
    return matchesSearch && matchesProjectType;
  });

  const getStatusColor = (status: 'active' | 'inactive' | 'maintenance') => {
    const colors = {
      active: 'text-green-600 bg-green-50 border-green-200',
      inactive: 'text-gray-600 bg-gray-50 border-gray-200',
      maintenance: 'text-yellow-600 bg-yellow-50 border-yellow-200'
    };
    return colors[status] || colors.active;
  };

  const getStatusLabel = (status: 'active' | 'inactive' | 'maintenance') => {
    const labels = {
      active: 'Activo',
      inactive: 'Inactivo',
      maintenance: 'Mantenimiento'
    };
    return labels[status] || status;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getProjectTypeIcon = (projectType: 'chatbot' | 'voicebot') => {
    return projectType === 'chatbot' ? '💬' : '📞';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} hr`;
    if (diffDays === 1) return 'Hace 1 día';
    return `Hace ${diffDays} días`;
  };

  const formatCost = (cost: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(cost);
  };

  // Calculate status counts
  const activeClients = clients.filter(c => c.status === 'active').length;
  const inactiveClients = clients.filter(c => c.status === 'inactive').length;
  const maintenanceClients = clients.filter(c => c.status === 'maintenance').length;
  const chatbotClients = clients.filter(c => c.project_type === 'chatbot').length;
  const voicebotClients = clients.filter(c => c.project_type === 'voicebot').length;

  // Error state
  if (error) {
    return (
      <AdminPageLayout title="Gestión de Clientes" subtitle="">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500 mb-2">Error al cargar clientes</p>
            <p className="text-sm text-gray-600">{(error as Error).message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="Gestión de Clientes"
      subtitle={isLoading ? 'Cargando...' : `${clientsData?.total || 0} clientes en total • ${activeClients} activos • ${inactiveClients + maintenanceClients} requieren atención`}
      actions={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setView(view === 'grid' ? 'table' : 'grid')}
          >
            {view === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
          </Button>
          <Button onClick={handleCreateClient}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </>
      }
      filters={
        <>
          <Input
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="maintenance">Mantenimiento</SelectItem>
            </SelectContent>
          </Select>
          <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo de Bot" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="chatbot">Chatbot 💬</SelectItem>
              <SelectItem value="voicebot">Voicebot 📞</SelectItem>
            </SelectContent>
          </Select>
        </>
      }
    >
      {/* Métricas de resumen */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clientes</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatNumber(clientsData?.total || 0)}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-600 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Activos</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatNumber(activeClients)}
                  </p>
                </div>
                <Users className="h-8 w-8 text-green-600 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Chatbot</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatNumber(chatbotClients)}
                  </p>
                </div>
                <span className="text-3xl">💬</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Voicebot</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatNumber(voicebotClients)}
                  </p>
                </div>
                <span className="text-3xl">📞</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Vista Grid */}
      {view === 'grid' && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredClients.map((client) => (
                <Card key={client.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium text-xl">
                          {getProjectTypeIcon(client.project_type)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{client.name}</CardTitle>
                          <CardDescription className="capitalize">
                            {client.project_type}
                          </CardDescription>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleViewDetails(client)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Detalles
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditClient(client)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteClient(client)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Client Info */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Tipo</p>
                        <p className="font-medium capitalize">{client.project_type}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Estado</p>
                        <Badge className={getStatusColor(client.status)}>
                          {getStatusLabel(client.status)}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Tag VAPI</p>
                        <p className="font-medium font-mono text-xs">{client.vapi_tag}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Proyecto ID</p>
                        <p className="font-medium font-mono text-xs">{client.supabase_project_id.slice(0, 8)}...</p>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                      <div>
                        <p>Creado: {formatDate(client.created_at)}</p>
                      </div>
                      <div>
                        <p>Actualizado: {formatDate(client.updated_at)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Vista Tabla (implementación simplificada) */}
      {view === 'table' && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b bg-muted/50">
                      <tr>
                        <th className="text-left p-4 font-medium">Cliente</th>
                        <th className="text-left p-4 font-medium">Tipo</th>
                        <th className="text-left p-4 font-medium">Estado</th>
                        <th className="text-left p-4 font-medium">Tag VAPI</th>
                        <th className="text-left p-4 font-medium">Creado</th>
                        <th className="text-left p-4 font-medium">Actualizado</th>
                        <th className="text-left p-4 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((client) => (
                        <tr key={client.id} className="border-b hover:bg-muted/30">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {getProjectTypeIcon(client.project_type)}
                              </div>
                              <div>
                                <div className="font-medium">{client.name}</div>
                                <div className="text-sm text-muted-foreground capitalize">{client.project_type}</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 capitalize">{client.project_type}</td>
                          <td className="p-4">
                            <Badge className={getStatusColor(client.status)}>
                              {getStatusLabel(client.status)}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <span className="font-mono text-xs">{client.vapi_tag}</span>
                          </td>
                          <td className="p-4">{formatDate(client.created_at)}</td>
                          <td className="p-4">{formatDate(client.updated_at)}</td>
                          <td className="p-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => handleViewDetails(client)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Ver Detalles
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditClient(client)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteClient(client)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* PHASE 1 & 2: Client Details Sheet with Tabs */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {selectedClient && (
                <>
                  <span className="text-2xl">
                    {getProjectTypeIcon(selectedClient.project_type)}
                  </span>
                  {selectedClient.name}
                </>
              )}
            </SheetTitle>
            <SheetDescription>
              Detalles completos del cliente
            </SheetDescription>
          </SheetHeader>

          {selectedClient && (
            <div className="mt-6">
              {/* PHASE 2: Tabs for organizing client details - Conditional based on project_type */}
              <Tabs defaultValue="info" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="info">Información</TabsTrigger>
                  {selectedClient.project_type === 'chatbot' && (
                    <TabsTrigger value="leads">Leads</TabsTrigger>
                  )}
                  {selectedClient.project_type === 'voicebot' && (
                    <TabsTrigger value="llamadas">Llamadas</TabsTrigger>
                  )}
                </TabsList>

                {/* Tab 1: Information */}
                <TabsContent value="info" className="space-y-6 mt-6">
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(selectedClient.status)}>
                      {getStatusLabel(selectedClient.status)}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {selectedClient.project_type}
                    </Badge>
                  </div>

                  {/* Leads Metrics for Chatbot */}
                  {selectedClient.project_type === 'chatbot' && leadsStatsData && (
                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Leads Totales</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {formatNumber(leadsStatsData.total)}
                              </p>
                            </div>
                            <Users className="h-6 w-6 text-blue-600 opacity-80" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Último Lead</p>
                            {leadsStatsData.latestLead ? (
                              <div>
                                <p className="text-sm font-medium truncate">
                                  {leadsStatsData.latestLead.full_name || leadsStatsData.latestLead.email || 'Sin nombre'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(leadsStatsData.latestLead.created_at).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">Sin leads</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Voicebot Metrics */}
                  {selectedClient.project_type === 'voicebot' && voicebotStatsData && (
                    <div className="grid grid-cols-3 gap-4">
                      <Card>
                        <CardContent className="p-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Total de Llamadas</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatNumber(voicebotStatsData.totalCalls)}
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Última Llamada</p>
                            {voicebotStatsData.latestTrace ? (
                              <div>
                                <p className="text-sm font-medium">
                                  {formatTimeAgo(voicebotStatsData.latestTrace.timestamp)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(voicebotStatsData.latestTrace.timestamp).toLocaleDateString('es-ES', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">Sin llamadas</p>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardContent className="p-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Costo Total Hoy</p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCost(voicebotStatsData.totalCostToday)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatNumber(voicebotStatsData.totalCallsToday)} llamadas
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Client Information */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">Información del Cliente</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">ID</p>
                          <p className="text-sm font-mono">{selectedClient.id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Tipo de Proyecto</p>
                          <p className="text-sm capitalize">{selectedClient.project_type}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-muted-foreground">Tag VAPI</p>
                          <p className="text-sm font-mono break-all">{selectedClient.vapi_tag}</p>
                        </div>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">Detalles Técnicos</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Supabase Project ID</p>
                          <p className="text-sm font-mono break-all">{selectedClient.supabase_project_id}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Vault Secret Name</p>
                          <p className="text-sm font-mono break-all">{selectedClient.vault_secret_name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3">Fechas</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Creado</p>
                          <p className="text-sm">{formatDate(selectedClient.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Última Actualización</p>
                          <p className="text-sm">{formatDate(selectedClient.updated_at)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button variant="outline" className="flex-1" onClick={() => handleEditClient(selectedClient)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button variant="outline" className="flex-1 text-red-600 hover:text-red-700" onClick={() => handleDeleteClient(selectedClient)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </TabsContent>

                {/* Tab 2: Leads (only for chatbot) */}
                {selectedClient.project_type === 'chatbot' && (
                  <TabsContent value="leads" className="mt-6">
                    <AdminClientLeadsTable clientId={selectedClient.id} />
                  </TabsContent>
                )}

                {/* Tab 2: Llamadas (only for voicebot) - with mock data fallback */}
                {selectedClient.project_type === 'voicebot' && (
                  <TabsContent value="llamadas" className="mt-6">
                    <AdminClientTracesTable vapiTag={selectedClient.vapi_tag} />
                  </TabsContent>
                )}
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* CRUD Dialogs */}
      <ClientFormDialog
        open={isFormDialogOpen}
        onOpenChange={setIsFormDialogOpen}
        client={clientToEdit}
        onSubmit={handleFormSubmit}
        loading={createClientMutation.isPending || updateClientMutation.isPending}
      />

      <DeleteClientDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        client={clientToDelete}
        onConfirm={handleDeleteConfirm}
        loading={deleteClientMutation.isPending}
      />
    </AdminPageLayout>
  );
}